/**
 * Firebase Wheel Service
 *
 * This service provides methods for interacting with the Wheels collection in Firestore.
 */
import { serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

import {
  convertToFirestoreWheel,
  SharedWheel,
  SharedWheelSpin,
  ShareSettings,
  Wheel,
  WheelTheme,
} from '../types/wheel';
import databaseService from './database';
import { auth } from './firebase';

// Collection name
const WHEELS_COLLECTION = 'wheels';
const WHEEL_SHARES_COLLECTION = 'wheelShares';

/**
 * Convert Wheel to Firestore format
 */
const toFirestoreFormat = (wheel: Partial<Wheel>): any => {
  // Convert entries to items format expected by Firestore
  const items = wheel.entries?.map(entry => {
    // Ensure metadata is never undefined - replace with empty object
    const metadata = entry.metadata || {};
    // Make sure there are no undefined values in metadata
    Object.keys(metadata).forEach(key => {
      if (metadata[key] === undefined) {
        metadata[key] = null;
      }
    });
    return {
      id: entry.id, // Preserve the entry ID
      text: entry.text || '',
      color: entry.color || null,
      emoji: entry.emoji || null,
      weight: entry.weight || 1,
      type: entry.type || 'text',
      metadata: metadata,
    };
  });

  // Create a complete wheelSettings object with all properties defined
  const allowDuplicateSpins = wheel.wheelSettings?.allowDuplicateSpins;
  const wheelStyle = wheel.wheelSettings?.wheelStyle || 'default';
  const customCss = wheel.wheelSettings?.customCss || '';
  const wheelSettings = {
    allowDuplicateSpins: allowDuplicateSpins !== undefined ? allowDuplicateSpins : true,
    wheelStyle,
    customCss,
  };

  return {
    name: wheel.name || '',
    description: wheel.description || '',
    // IMPORTANT: Use 'entries' not 'items' to match the Firestore rules
    entries: items || [],
    theme: wheel.theme || WheelTheme.DEFAULT,
    custom_colors: wheel.custom_colors || [],
    isPublic: wheel.isPublic !== undefined ? wheel.isPublic : false,
    category: wheel.category || 'custom',
    difficulty: wheel.difficulty || 1,
    tags: wheel.tags || [],
    wheelSettings,
  };
};

/**
 * Convert Firestore document to Wheel type
 */
const fromFirestoreFormat = (data: any): Wheel => {
  // Handle both old (items) and new (entries) field names for backward compatibility
  const entriesData = data.entries || data.items || [];

  return {
    id: data.id,
    name: data.name,
    description: data.description || '',
    entries: entriesData.map((entry: any) => ({
      id: entry.id || crypto.randomUUID(),
      text: entry.text,
      type: entry.type || 'text',
      color: entry.color,
      emoji: entry.emoji,
      weight: entry.weight || 1,
      metadata: entry.metadata || {},
    })),
    theme: data.theme || WheelTheme.DEFAULT,
    custom_colors: data.custom_colors || [],
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    createdBy: data.createdBy,
    isPublic: data.isPublic || false,
    category: data.category || 'custom',
    difficulty: data.difficulty || 1,
    spinCount: data.spinCount || 0,
    favorites: data.favorites || 0,
    tags: data.tags || [],
    wheelSettings: data.wheelSettings || {
      allowDuplicateSpins: true,
      wheelStyle: 'default',
    },
  };
};

/**
 * Create a new wheel in Firestore
 */
export const createWheel = async (wheelData: Partial<Wheel>): Promise<string> => {
  try {
    const userId = auth.currentUser?.uid;

    // Create a properly formatted wheel with no undefined values
    const wheelToCreate = {
      ...toFirestoreFormat(wheelData),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId || null,
      spinCount: 0,
      favorites: 0,
      // Ensure tokens are always defined
      access_token: uuidv4(),
      public_token: wheelData.isPublic ? uuidv4() : null,
    };

    // Sanitize the object to remove any nested undefined values
    const sanitizedWheel = JSON.parse(JSON.stringify(wheelToCreate));

    const result = await databaseService.create(WHEELS_COLLECTION, sanitizedWheel);
    return result.id;
  } catch (error) {
    console.error('Error creating wheel in Firestore:', error);
    throw new Error('Failed to create wheel');
  }
};

/**
 * Get a wheel by ID from Firestore
 */
export const getWheel = async (wheelId: string): Promise<Wheel | null> => {
  try {
    const result = await databaseService.get(WHEELS_COLLECTION, wheelId);

    if (!result) {
      return null;
    }

    return fromFirestoreFormat(result);
  } catch (error) {
    console.error('Error getting wheel from Firestore:', error);
    throw new Error('Failed to get wheel');
  }
};

/**
 * Update a wheel in Firestore
 */
export const updateWheel = async (wheelId: string, wheelData: Partial<Wheel>): Promise<boolean> => {
  try {
    // Check if user is authenticated
    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw new Error('User must be authenticated to update a wheel');
    }

    // Get the current wheel to verify ownership
    const currentWheel = await getWheel(wheelId);
    if (!currentWheel) {
      throw new Error('Wheel not found');
    }

    // Verify the user is the creator of the wheel
    if (currentWheel.createdBy !== userId) {
      throw new Error('Only the wheel creator can update this wheel');
    }

    // Ensure these fields can't be updated directly
    const { id, createdAt, createdBy, spinCount, favorites, ...updateData } = wheelData;

    // Ensure these fields can't be updated directly

    // Special handling for shareSettings updates
    if (updateData.shareSettings && Object.keys(updateData).length === 1) {
      // Special handling for shareSettings updates
      const updatePayload = {
        shareSettings: updateData.shareSettings,
        updatedAt: serverTimestamp(),
      };

      const sanitizedUpdatePayload = JSON.parse(JSON.stringify(updatePayload));
      await databaseService.update(WHEELS_COLLECTION, wheelId, sanitizedUpdatePayload);
      return true;
    }

    // Special handling for entries updates
    if (updateData.entries) {
      // Create a minimal update payload with just entries and updatedAt
      const updatePayload: any = {
        updatedAt: serverTimestamp(),
      };

      // If we're updating entries, ensure they're properly formatted
      if (updateData.entries && Array.isArray(updateData.entries)) {
        // Validate and format entries
        const validatedEntries = updateData.entries.map((entry: any) => {
          return {
            id: entry.id || `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: entry.text || '',
            color: entry.color || null,
            emoji: entry.emoji || null,
            weight: entry.weight || 1,
            type: entry.type || 'text',
            metadata: entry.metadata || {},
          };
        });

        // Ensure we have at least one entry (required by security rules)
        if (validatedEntries.length === 0) {
          throw new Error('Entries array cannot be empty');
        }

        updatePayload.entries = validatedEntries;
      }

      // Include name if provided
      if (updateData.name) {
        updatePayload.name = updateData.name;
      }

      // Sanitize the data

      // Sanitize the data
      const sanitizedPayload = JSON.parse(JSON.stringify(updatePayload));

      try {
        await databaseService.update(WHEELS_COLLECTION, wheelId, sanitizedPayload);
        // Update successful
        return true;
      } catch (updateError) {
        console.error('Update failed:', updateError);

        // If simplified update fails, try with the full document

        // Start with the current wheel data
        const fullUpdateData = { ...currentWheel };

        // Update with the new data
        if (updateData.entries) {
          fullUpdateData.entries = updatePayload.entries;
        }

        if (updateData.name) {
          fullUpdateData.name = updateData.name;
        }

        // Set the updated timestamp
        (fullUpdateData as any).updatedAt = serverTimestamp();

        // Remove the id field as it's not part of the document data
        const { id, ...documentData } = fullUpdateData;

        // Format the data for Firestore
        const formattedData = toFirestoreFormat(documentData as Wheel);

        // Add back fields that toFirestoreFormat doesn't include
        formattedData.createdBy = currentWheel.createdBy;
        formattedData.createdAt = currentWheel.createdAt;
        formattedData.updatedAt = serverTimestamp();
        formattedData.spinCount = currentWheel.spinCount;
        formattedData.favorites = currentWheel.favorites;

        if (currentWheel.shareSettings) {
          formattedData.shareSettings = currentWheel.shareSettings;
        }

        // Sanitize the data
        const sanitizedData = JSON.parse(JSON.stringify(formattedData));

        await databaseService.update(WHEELS_COLLECTION, wheelId, sanitizedData);
        return true;
      }
    }

    // For general updates, merge with the current wheel data to ensure all required fields are present
    // This is necessary because the Firestore security rules validate the entire document

    // For general updates, merge with the current wheel data to ensure all required fields are present

    const mergedData = {
      ...currentWheel,
      ...updateData,
      // Preserve these fields from the original wheel
      id: currentWheel.id,
      createdAt: currentWheel.createdAt,
      createdBy: currentWheel.createdBy,
      spinCount: currentWheel.spinCount,
      favorites: currentWheel.favorites,
    };

    // Format the merged data - we need to cast to avoid TypeScript errors with updatedAt
    const formattedData: any = toFirestoreFormat(mergedData as Wheel);

    // Add back fields that toFirestoreFormat doesn't include
    formattedData.createdBy = currentWheel.createdBy;
    formattedData.createdAt = currentWheel.createdAt;
    formattedData.updatedAt = serverTimestamp();
    formattedData.spinCount = currentWheel.spinCount;
    formattedData.favorites = currentWheel.favorites;

    // Ensure entries field is properly named and structured
    // This is critical for Firebase security rules validation
    if (formattedData.entries) {
      // Make sure each entry has an ID
      formattedData.entries = formattedData.entries.map((entry: any, index: number) => {
        if (!entry.id) {
          entry.id = `entry-${index}-${Date.now()}`;
        }
        return entry;
      });
    }

    if (currentWheel.shareSettings) {
      formattedData.shareSettings = currentWheel.shareSettings;
    }

    // Sanitize the object to remove any nested undefined values
    const sanitizedData = JSON.parse(JSON.stringify(formattedData));

    // Ensure data is properly sanitized

    // Double-check that entries field is properly structured for Firestore rules
    if (!sanitizedData.entries || !Array.isArray(sanitizedData.entries)) {
      throw new Error('Invalid entries format');
    }

    // Ensure all required fields are present according to Firestore rules
    const requiredFields = ['name', 'entries', 'createdBy', 'isPublic'];
    for (const field of requiredFields) {
      if (sanitizedData[field] === undefined) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    // Try a more targeted approach first - only update specific fields
    // This might help with permission issues
    const minimalUpdatePayload: any = {
      updatedAt: serverTimestamp(),
    };

    // Only include entries if they were explicitly provided in the update
    if (updateData.entries) {
      minimalUpdatePayload.entries = sanitizedData.entries;
    }

    // Only include name if it was explicitly provided in the update
    if (updateData.name !== undefined) {
      minimalUpdatePayload.name = sanitizedData.name;
    }

    // Include other fields that were explicitly updated
    Object.keys(updateData).forEach(key => {
      if (
        key !== 'entries' &&
        key !== 'name' &&
        key !== 'id' &&
        key !== 'createdAt' &&
        key !== 'createdBy' &&
        key !== 'spinCount' &&
        key !== 'favorites'
      ) {
        minimalUpdatePayload[key] = sanitizedData[key];
      }
    });

    try {
      await databaseService.update(WHEELS_COLLECTION, wheelId, minimalUpdatePayload);
      return true;
    } catch (minimalUpdateError) {
      // If minimal update fails, try the full update
      await databaseService.update(WHEELS_COLLECTION, wheelId, sanitizedData);
    }
    return true;
  } catch (error) {
    console.error('Error updating wheel in Firestore:', error);
    throw new Error('Failed to update wheel');
  }
};

/**
 * Delete a wheel from Firestore
 */
export const deleteWheel = async (wheelId: string): Promise<boolean> => {
  try {
    return await databaseService.delete(WHEELS_COLLECTION, wheelId);
  } catch (error) {
    console.error('Error deleting wheel from Firestore:', error);
    throw new Error('Failed to delete wheel');
  }
};

/**
 * List wheels with optional filtering
 */
export const listWheels = async (
  filters: Record<string, any> = {},
  pageSize = 20
): Promise<Wheel[]> => {
  try {
    // Convert filters to query conditions
    const conditions = Object.entries(filters).map(([field, value]) => ({
      field,
      operator: '==',
      value,
    })) as Array<{
      field: string;
      operator: any;
      value: any;
    }>;

    // Ensure we have either isPublic=true or createdBy=currentUserId filter
    // as required by security rules
    const hasPublicFilter = conditions.some(
      condition => condition.field === 'isPublic' && condition.value === true
    );
    const hasCreatedByFilter = conditions.some(condition => condition.field === 'createdBy');

    const userId = auth.currentUser?.uid;

    if (!hasPublicFilter && !hasCreatedByFilter && userId) {
      // If no required filter is present, add createdBy filter with current user ID
      conditions.push({
        field: 'createdBy',
        operator: '==',
        value: userId,
      });
    }

    // Apply limit to comply with security rules
    const limitValue = pageSize > 100 ? 100 : pageSize;

    // Query with conditions
    const results = await databaseService.query(WHEELS_COLLECTION, conditions);

    // Apply limit in memory since we can't combine where and limit in the query
    const limitedResults = results.slice(0, limitValue);

    return limitedResults.map(fromFirestoreFormat);
  } catch (error) {
    console.error('Error listing wheels from Firestore:', error);
    throw new Error('Failed to list wheels');
  }
};

/**
 * Get wheels created by a specific user
 */
export const getUserWheels = async (userId: string, pageSize = 20): Promise<Wheel[]> => {
  return listWheels({ createdBy: userId }, pageSize);
};

/**
 * Get public wheels
 */
export const getPublicWheels = async (pageSize = 20): Promise<Wheel[]> => {
  return listWheels({ isPublic: true }, pageSize);
};

/**
 * Get wheels by category
 */
export const getWheelsByCategory = async (category: string, pageSize = 20): Promise<Wheel[]> => {
  return listWheels({ category, isPublic: true }, pageSize);
};

/**
 * Get popular wheels
 */
export const getPopularWheels = async (pageSize = 20): Promise<Wheel[]> => {
  try {
    // This is a simplified implementation
    // In a real application, you would use a Firestore query with ordering
    const wheels = await getPublicWheels(100); // Get a larger set to sort from
    return wheels.sort((a, b) => b.spinCount - a.spinCount).slice(0, pageSize);
  } catch (error) {
    console.error('Error getting popular wheels from Firestore:', error);
    throw new Error('Failed to get popular wheels');
  }
};

/**
 * Increment the spin count for a wheel
 */
export const incrementSpinCount = async (wheelId: string): Promise<boolean> => {
  try {
    // Get current wheel
    const wheel = await getWheel(wheelId);
    if (!wheel) {
      return false;
    }

    // Update with incremented spin count
    await databaseService.update(WHEELS_COLLECTION, wheelId, {
      spinCount: (wheel.spinCount || 0) + 1,
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error('Error incrementing spin count in Firestore:', error);
    throw new Error('Failed to increment spin count');
  }
};

/**
 * Increment the favorites count for a wheel
 */
export const incrementFavorites = async (wheelId: string): Promise<boolean> => {
  try {
    // Get current wheel
    const wheel = await getWheel(wheelId);
    if (!wheel) {
      return false;
    }

    // Update with incremented favorites count
    await databaseService.update(WHEELS_COLLECTION, wheelId, {
      favorites: (wheel.favorites || 0) + 1,
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error('Error incrementing favorites in Firestore:', error);
    throw new Error('Failed to increment favorites');
  }
};

/**
 * Migrate a wheel from localStorage to Firestore
 */
export const migrateWheelToFirestore = async (localWheel: any): Promise<string> => {
  try {
    const userId = auth.currentUser?.uid;

    if (!userId) {
      throw new Error('User must be authenticated to migrate wheels');
    }

    // Convert local wheel to Firestore format
    const firestoreWheel = convertToFirestoreWheel(localWheel);

    // Add user ID
    firestoreWheel.createdBy = userId;

    // Create in Firestore
    return await createWheel(firestoreWheel);
  } catch (error) {
    console.error('Error migrating wheel to Firestore:', error);
    throw new Error('Failed to migrate wheel');
  }
};

/**
 * Create a new wheel share
 * @param wheelId The ID of the wheel to share
 * @param shareSettings The settings for the share
 * @returns The ID of the created share
 */
export const createWheelShare = async (
  wheelId: string,
  shareSettings: ShareSettings
): Promise<string> => {
  try {
    const userId = auth.currentUser?.uid;

    if (!userId) {
      throw new Error('User must be authenticated to create a wheel share');
    }

    // Verify the wheel exists and user owns it
    const wheel = await getWheel(wheelId);
    if (!wheel) {
      throw new Error('Wheel not found');
    }

    if (wheel.createdBy !== userId) {
      throw new Error('Only the wheel owner can create shares');
    }

    // Create the shared wheel document
    const sharedWheel: SharedWheel = {
      parentWheelId: wheelId,
      shareId: shareSettings.shareId,
      ownerId: userId,
      settings: shareSettings,
      spins: [],
      currentSpinCount: 0,
    };

    // Save to Firestore - explicitly set the document ID to match the shareId
    await databaseService.create(WHEEL_SHARES_COLLECTION, {
      ...sharedWheel,
      id: shareSettings.shareId, // This will be used as the document ID
    });

    // Update the wheel with the share settings
    const updatedShareSettings = wheel.shareSettings || [];
    updatedShareSettings.push(shareSettings);

    await updateWheel(wheelId, {
      shareSettings: updatedShareSettings,
    });

    return shareSettings.shareId;
  } catch (error) {
    console.error('Error creating wheel share:', error);
    throw new Error('Failed to create wheel share');
  }
};

/**
 * Get a shared wheel by ID
 * @param shareId The ID of the shared wheel
 * @returns The shared wheel or null if not found
 */
export const getSharedWheel = async (shareId: string): Promise<SharedWheel | null> => {
  try {
    // Get the document using shareId as the document ID
    const result = await databaseService.get(WHEEL_SHARES_COLLECTION, shareId);

    if (!result) {
      // If not found by ID, try querying by shareId field as a fallback
      // This handles cases where the document ID doesn't match the shareId
      const results = await databaseService.query(WHEEL_SHARES_COLLECTION, [
        { field: 'shareId', operator: '==', value: shareId },
      ]);
      if (results && results.length > 0) {
        return results[0] as SharedWheel;
      }
      return null;
    }

    return result as SharedWheel;
  } catch (error) {
    console.error('Error getting shared wheel:', error);
    throw new Error('Failed to get shared wheel');
  }
};

/**
 * Get all shares for a wheel
 * @param wheelId The ID of the wheel
 * @returns Array of shared wheels
 */
export const getWheelShares = async (wheelId: string): Promise<SharedWheel[]> => {
  try {
    const results = await databaseService.query(WHEEL_SHARES_COLLECTION, [
      { field: 'parentWheelId', operator: '==', value: wheelId },
    ]);

    return results as SharedWheel[];
  } catch (error) {
    console.error('Error getting wheel shares:', error);
    throw new Error('Failed to get wheel shares');
  }
};

/**
 * Update a wheel share
 * @param wheelId The ID of the wheel
 * @param shareId The ID of the share to update
 * @param settings The updated settings
 * @returns True if successful
 */
export const updateWheelShare = async (
  wheelId: string,
  shareId: string,
  settings: Partial<ShareSettings>
): Promise<boolean> => {
  try {
    const userId = auth.currentUser?.uid;

    if (!userId) {
      throw new Error('User must be authenticated to update a wheel share');
    }

    // Verify the wheel exists and user owns it
    const wheel = await getWheel(wheelId);
    if (!wheel) {
      throw new Error('Wheel not found');
    }

    if (wheel.createdBy !== userId) {
      throw new Error('Only the wheel owner can update shares');
    }

    // Update the shared wheel document
    await databaseService.update(WHEEL_SHARES_COLLECTION, shareId, { settings });

    // Update the wheel's share settings
    if (wheel.shareSettings) {
      const updatedShareSettings = wheel.shareSettings.map(shareSetting => {
        if (shareSetting.shareId === shareId) {
          return { ...shareSetting, ...settings };
        }
        return shareSetting;
      });

      await updateWheel(wheelId, {
        shareSettings: updatedShareSettings,
      });
    }

    return true;
  } catch (error) {
    console.error('Error updating wheel share:', error);
    throw new Error('Failed to update wheel share');
  }
};

/**
 * Delete a wheel share
 * @param wheelId The ID of the wheel
 * @param shareId The ID of the share to delete
 * @returns True if successful
 */
export const deleteWheelShare = async (wheelId: string, shareId: string): Promise<boolean> => {
  try {
    const userId = auth.currentUser?.uid;

    if (!userId) {
      throw new Error('User must be authenticated to delete a wheel share');
    }

    // Verify the wheel exists and user owns it
    const wheel = await getWheel(wheelId);
    if (!wheel) {
      throw new Error('Wheel not found');
    }

    if (wheel.createdBy !== userId) {
      throw new Error('Only the wheel owner can delete shares');
    }

    // Delete the shared wheel document
    await databaseService.delete(WHEEL_SHARES_COLLECTION, shareId);

    // Update the wheel's share settings
    if (wheel.shareSettings) {
      const updatedShareSettings = wheel.shareSettings.filter(
        shareSetting => shareSetting.shareId !== shareId
      );

      await updateWheel(wheelId, {
        shareSettings: updatedShareSettings,
      });
    }

    return true;
  } catch (error) {
    console.error('Error deleting wheel share:', error);
    throw new Error('Failed to delete wheel share');
  }
};

/**
 * Record a spin on a shared wheel
 * @param shareId The ID of the shared wheel
 * @param spinData The spin data to record
 * @returns True if successful
 */
export const recordSharedWheelSpin = async (
  shareId: string,
  spinData: SharedWheelSpin
): Promise<boolean> => {
  try {
    // Get the shared wheel
    const sharedWheel = await getSharedWheel(shareId);
    if (!sharedWheel) {
      throw new Error('Shared wheel not found');
    }

    // Check if max spins has been reached
    if (
      sharedWheel.settings.maxSpins !== null &&
      sharedWheel.currentSpinCount >= sharedWheel.settings.maxSpins
    ) {
      throw new Error('Maximum number of spins reached');
    }

    // Add the spin to the spins subcollection
    await databaseService.create(`${WHEEL_SHARES_COLLECTION}/${shareId}/spins`, spinData);

    // Update the current spin count
    await databaseService.update(WHEEL_SHARES_COLLECTION, shareId, {
      currentSpinCount: sharedWheel.currentSpinCount + 1,
    });

    // Also increment the spin count on the parent wheel
    await incrementSpinCount(sharedWheel.parentWheelId);

    return true;
  } catch (error) {
    console.error('Error recording shared wheel spin:', error);
    throw new Error('Failed to record shared wheel spin');
  }
};

/**
 * Get all spins for a shared wheel
 * @param shareId The ID of the shared wheel
 * @returns Array of spins
 */
export const getSharedWheelSpins = async (shareId: string): Promise<SharedWheelSpin[]> => {
  try {
    const results = await databaseService.list(`${WHEEL_SHARES_COLLECTION}/${shareId}/spins`);
    return results as SharedWheelSpin[];
  } catch (error) {
    console.error('Error getting shared wheel spins:', error);
    throw new Error('Failed to get shared wheel spins');
  }
};
